import { useForm, useFormContext, useWatch } from "react-hook-form";

import type { TicketStatusDetailsDto } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { Form } from "@/components/Form/Form";
import { FormCheckbox } from "@/components/Form/FormCheckbox";
import { FormColorPicker } from "@/components/Form/FormColorPicker";
import { FormInput } from "@/components/Form/FormInput";
import { FormSelect } from "@/components/Form/FormSelect";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { useSlug } from "@/hooks/useSlug";
import { getTicketStatusTranslation } from "@/modules/ticket-statuses/util/ticketStatusHelper";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export interface TicketStatusFormProps {
  isEditMode: boolean;
  defaultFormValues: Partial<FormValues>;
  isSubmitting: boolean;
  ticketStatuses: TicketStatusDetailsDto[];
  onSubmit: (data: FormValues) => void;
}

export function TicketStatusForm({
  ticketStatuses,
  isEditMode,
  defaultFormValues,
  onSubmit,
  isSubmitting,
}: TicketStatusFormProps): React.ReactNode {
  const { t } = useTranslation();
  const slug = useSlug();
  const formMethods = useForm<FormValues>({ defaultValues: defaultFormValues });
  const title = isEditMode ? t("page.ticket-status.edit.title") : t("page.ticket-status.create.title");

  return (
    <DocumentPage
      containerBoundary="inherit"
      contentSize="constrained"
      tabTitle={title}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.ticket-status.list.title"),
              to: routes.ticketStatuses.list({ slug }),
            },
            {
              name: title,
            },
          ]}
        />
      }
    >
      <Form.Root formMethods={formMethods} onSubmit={onSubmit} data-testid="ticket-status-form">
        <Form.Container>
          <CreateOrEditForm
            isEditMode={isEditMode}
            ticketStatuses={ticketStatuses}
            isSubmitting={isSubmitting}
            defaultValues={defaultFormValues}
          />
        </Form.Container>
      </Form.Root>
    </DocumentPage>
  );
}

function CreateOrEditForm({
  isEditMode,
  isSubmitting,
  ticketStatuses,
  defaultValues,
}: {
  isEditMode: boolean;
  isSubmitting: boolean;
  ticketStatuses: TicketStatusFormProps["ticketStatuses"];
  defaultValues: TicketStatusFormProps["defaultFormValues"];
}) {
  const formMethods = useFormContext<FormValues>();
  const type = useWatch<FormValues, "type">({ control: formMethods.control, name: "type" });

  const { setValue, getValues } = formMethods;
  const { t } = useTranslation();

  const editTypeDisabled = isEditMode && ticketStatuses.filter((x) => x.type === defaultValues.type).length <= 1;

  function setColorToStatusColorIfNotDirty(newType?: FormValues["type"]) {
    if (!newType) {
      return;
    }

    const currentColor = getValues("color");
    if (type && DEFAULT_COLORS[type] !== currentColor?.toUpperCase()) {
      // if the color is already set to a custom color, don't change it
      return;
    }

    const newColor = DEFAULT_COLORS[newType];
    if (newColor) {
      setValue("color", newColor, { shouldDirty: false });
    }
  }

  return (
    <>
      <Form.Body>
        <FormField label={t("page.ticket-status.form.name")} required>
          <FormInput<FormValues>
            data-testid="ticket-status-name"
            name="name"
            rules={{
              required: {
                message: t("components.form.error.required", {
                  inputName: t("page.ticket-status.form.name"),
                }),
                value: true,
              },
              maxLength: {
                message: t("components.form.error.max-length", {
                  length: NAME_MAX_LENGTH,
                }),
                value: NAME_MAX_LENGTH,
              },
            }}
          />
        </FormField>
        <FormField label={t("page.ticket-status.form.type")} required>
          <FormSelect<FormValues, FormValues["type"]>
            name="type"
            // Can't change type to rated (because we would need a rating)
            items={isEditMode && defaultValues.type !== "rated" ? TYPES.filter((x) => x !== "rated") : TYPES}
            placeholder={t("page.ticket-status.form.type.placeholder")}
            keySelector={(x) => x}
            disabled={editTypeDisabled}
            renderOption={(x) => getTicketStatusTranslation(t, x)}
            rules={{
              required: t("components.form.error.required", {
                inputName: t("page.ticket-status.form.type"),
              }),
            }}
            onChange={setColorToStatusColorIfNotDirty}
          />
        </FormField>
        <FormField label={t("page.ticket-status.form.color")} required>
          <FormColorPicker<FormValues, "color"> name="color" />
        </FormField>
        <FormField
          label={t("page.ticket-status.form.default-in-status")}
          description={t("page.ticket-status.form.default-in-status.description")}
        >
          <FormCheckbox<FormValues> name="isDefault" label={t("page.ticket-status.form.default-in-status.label")} />
        </FormField>
      </Form.Body>
      <Form.Footer
        renderPrimaryAction={() => (
          <Button type="submit" isLoading={isSubmitting}>
            {isEditMode ? t("page.ticket-status.edit.submit") : t("page.ticket-status.create.submit")}
          </Button>
        )}
      />
    </>
  );
}

const DEFAULT_COLORS: Record<FormValues["type"], string> = {
  autoClosed: "#00FFFF",
  rejected: "#F90608",
  rated: "#3EC605",
  closed: "#3C9AFF",
  inProgress: "#FA7908",
  new: "#9E9E9E",
};

const TYPES = ["new", "inProgress", "closed", "rated", "rejected", "autoClosed"] as FormValues["type"][];

const NAME_MAX_LENGTH = 30;

export interface FormValues {
  name: string;
  type: TicketStatusDetailsDto["type"];
  isDefault: boolean;
  color?: string;
}
